import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, message, Modal, Space } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import axios, { type AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import moment from 'moment';
import React from 'react';
import { mutate } from 'swr';

import PushNotification from '../PushNotification';
import type { TimeWithUnit } from '../sharedTypes';
import {
  bookingListAPIUrl,
  BookingListTypeConst,
  BookingPageTypeConst,
} from './constants';
import type {
  CleanBookingListResponse,
  CleanReservationRuleResponse,
  RangeValue,
} from './types';

const { confirm } = Modal;

export const fetchBookingList = async (
  url: string,
  searchType:
    | typeof BookingListTypeConst.Reserve
    | typeof BookingListTypeConst.History
) => {
  try {
    const res: AxiosResponse<CleanBookingListResponse[]> = await axios({
      method: 'POST',
      url,
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
      data: {
        reservationType: BookingPageTypeConst.Contractor,
        searchType,
      },
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response.data);
  }
};

export const fetchReservationRules = async (url: string) => {
  try {
    const res: AxiosResponse<CleanReservationRuleResponse> = await axios({
      method: 'GET',
      url,
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response.data);
  }
};

export const getLatestBookingList = (
  bookingListType:
    | typeof BookingListTypeConst.Reserve
    | typeof BookingListTypeConst.History
) => {
  mutate(
    bookingListAPIUrl,
    fetchBookingList(bookingListAPIUrl, bookingListType)
  );
};

const cancelOrder = async (
  orderId: string,
  bookingListType:
    | typeof BookingListTypeConst.Reserve
    | typeof BookingListTypeConst.History
) => {
  try {
    await axios({
      method: 'PATCH',
      url: '/ezcon/api/cancelCleanReservation',
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
      data: {
        orderId,
        isSupervisor: true,
      },
    });
    getLatestBookingList(bookingListType);
    message.success('成功取消訂單!');
  } catch (error) {
    message.success('取消訂單失敗!');
  }
};

const showCancelAlert = (args: {
  orderId: string;
  reservationDate: number;
  mustCancelBefore: TimeWithUnit | undefined;
  bookingListType:
    | typeof BookingListTypeConst.Reserve
    | typeof BookingListTypeConst.History;
}) => {
  const { orderId, reservationDate, mustCancelBefore, bookingListType } = args;

  let isCanRefund = false;

  if (mustCancelBefore) {
    const { value, unit } = mustCancelBefore;
    isCanRefund = isCanRefund = dayjs().isBefore(
      dayjs(reservationDate).subtract(value, unit)
    );
  }

  confirm({
    title: '確定取消此筆訂單？',
    icon: <ExclamationCircleOutlined />,
    content: isCanRefund
      ? '取消後將自動全額退款'
      : '已過全額退費時限，取消後無法自動退款',
    async onOk() {
      await cancelOrder(orderId, bookingListType);
    },
  });
};

export const getBookingListColumns = (
  mustCancelBefore: TimeWithUnit | undefined,
  bookingListType:
    | typeof BookingListTypeConst.Reserve
    | typeof BookingListTypeConst.History
) => {
  const columns: ColumnsType<CleanBookingListResponse> = [
    {
      title: '訂單編號',
      dataIndex: 'orderNumber',
    },
    {
      title: '下單時間',
      dataIndex: 'createdTime',
      sorter: (a, b) => a.createdTime - b.createdTime,
      render: (value) => dayjs(value).format('YYYY/MM/DD HH:mm'),
    },
    {
      title: '預約日期',
      dataIndex: 'reservationDate',
      sorter: (a, b) => a.reservationDate - b.reservationDate,
      render: (value) => dayjs(value).format('YYYY/MM/DD'),
    },
    {
      title: '會員',
      dataIndex: 'userName',
    },
    {
      title: '社區',
      dataIndex: 'communityName',
    },
    {
      title: '戶號',
      dataIndex: 'householdNumber',
    },
    {
      title: '手機號碼',
      dataIndex: 'phoneNumber',
    },
    {
      title: '狀態',
      dataIndex: 'status',
    },
    {
      title: '上次推播時間',
      dataIndex: 'lastMessageSendTime',
      render: (value) =>
        value ? dayjs(value).format('YYYY/MM/DD HH:mm') : value,
    },
    {
      title: '類別',
      dataIndex: 'cleanKindName',
    },
    {
      title: '項目',
      dataIndex: 'cleanServiceItem',
    },
    {
      title: '清潔人員',
      dataIndex: 'cleaner',
    },
    {
      title: '付費方式',
      dataIndex: 'paymentType',
    },
    {
      title: '金額',
      dataIndex: 'price',
    },
    {
      title: '操作',
      dataIndex: 'actions',
      fixed: 'right',
      render: (_value, record) => {
        return bookingListType === '預約清單' ? (
          <Space>
            <Button>查看編輯</Button>
            <Button
              type="primary"
              danger
              onClick={() =>
                showCancelAlert({
                  orderId: record.orderId,
                  reservationDate: record.reservationDate,
                  mustCancelBefore,
                  bookingListType,
                })
              }
            >
              取消
            </Button>
            <PushNotification
              userId={record.userId}
              cleanBookingsId={record.cleanBookingsId}
              bookingListType={bookingListType}
            />
          </Space>
        ) : record.status === '已清潔' ? (
          <Button>查看編輯</Button>
        ) : (
          <Button type="primary">查看</Button>
        );
      },
    },
  ];

  return columns;
};

export const showTableBookingList = (args: {
  cleanBookingList: CleanBookingListResponse[] | undefined;
  phoneNumberInput: string;
  communityName: string;
  cleanKindName: string;
  rangeOfDate: RangeValue;
}) => {
  const {
    cleanBookingList,
    phoneNumberInput,
    communityName,
    cleanKindName,
    rangeOfDate,
  } = args;
  let list = cleanBookingList;
  if (phoneNumberInput) {
    list = list?.filter((booking) =>
      booking.phoneNumber.includes(phoneNumberInput)
    );
  }
  if (communityName) {
    list = list?.filter((booking) => booking.communityName === communityName);
  }
  if (cleanKindName) {
    list = list?.filter((booking) => booking.cleanKindName === cleanKindName);
  }
  if (rangeOfDate) {
    list = list?.filter((booking) => {
      const isDateBetweenRange = moment(booking.reservationDate).isBetween(
        rangeOfDate[0]?.startOf('date'),
        rangeOfDate[1]?.endOf('date'),
        'date',
        '[]'
      );
      return isDateBetweenRange;
    });
  }
  return list;
};
